"use client"

import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { ModalSidebar } from "./modal-sidebar"
import { DetailsSection } from "./details-section"
import { StatusSection } from "./status-section"
import { PaymentSection } from "./payment-section"
import { NotificationsSection } from "./notifications-section"
import { ConsumablesSection } from "./consumables-section"
import { ClientDataSection } from "./client-data-section"
import { ClientNotesSection } from "./client-notes-section"
import { VisitHistorySection } from "./visit-history-section"
import { MessagesSection } from "./messages-section"
import { ChangesSection } from "./changes-section"
import { ModalFooter } from "./modal-footer"

import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { useWaitlistRefetch } from "@/hooks/calendar/useWaitlistMatches";
import { useAppointmentModalDetailsInfo } from "@/hooks/modal/useAppointmentModalDetailsInfo"
import { useEditAppointmentRecord } from "@/hooks/modal/useEditAppointmentRecord"
import { useIsMobile } from "@/hooks/use-mobile"
import { AppointmentModalMobile } from "./appointment-modal-mobile"
import { useComputedPayment } from "@/hooks/modal/useComputedPayment"

export function AppointmentModal() {
  const isMobile = useIsMobile()

  // Render mobile version for small screens
  if (isMobile) {
    return <AppointmentModalMobile />
  }

  // Render desktop version for larger screens
  return <AppointmentModalDesktop />
}

function AppointmentModalDesktop() {
  const { t } = useTranslation();
  const waitlistRefetch = useWaitlistRefetch();
  const {
    open,
    appointmentId,
    appointment,
    closeModal,
    setAppointment,
  } = useAppointmentModal();

  const { data: appointmentData, isLoading: isDetailsLoading } = useAppointmentModalDetailsInfo();

  // Initialize context appointment when data is loaded
  useEffect(() => {
    if (appointmentId && appointmentData) {
      setAppointment(appointmentData);
    }
  }, [appointmentId, appointmentData, setAppointment]);

  // When record disappears (deleted optimistically), close the modal
  useEffect(() => {
    if (appointmentId && !isDetailsLoading && appointmentData === null) {
      closeModal();
    }
  }, [appointmentId, isDetailsLoading, appointmentData, closeModal]);

  const [activeSection, setActiveSection] = useState("status")

  const { mutate: editAppointment, isPending: isEditing } = useEditAppointmentRecord();
  const payment = useComputedPayment();
  const paymentMethods = appointment?.paymentSection?.paymentMethods ?? [];
  const paidTotal = paymentMethods.reduce(
    (sum, m: any) => sum + (Number(m?.account?.paid_amount) || 0),
    0
  );
  const positiveCount = paymentMethods.filter((m: any) => Number(m?.account?.paid_amount) > 0).length;
  const totalAmount = payment?.totalAmount ?? 0;
  const isSplitSelection =
    (appointment?.paymentSection as any)?.paymentMode === "split" || positiveCount > 1;
  const isSplitInvalid =
    paymentMethods.length > 0 && isSplitSelection && Math.abs(paidTotal - totalAmount) >= 0.01;

  const handleDelete = () => {
    if (!appointment) return;
    editAppointment({ ...(appointment as any), deleted: 1 }, { onSuccess: () => {
      closeModal();
      // Cancel-moment prompt (waitlist README §4.6): freed capacity — show
      // waiting clients immediately, not on the next poll.
      waitlistRefetch();
    } });
  };

  const handleSave = () => {
    if (!appointment) return;
    if (isSplitInvalid) {
      alert(t("appointment.modal.split_payment_error", "Payment type amounts must equal the total amount"));
      return;
    }
    editAppointment(appointment, {
      onSuccess: () => {
        closeModal();
      },
      onError: (error: any) => {
        // Save used to fail SILENTLY (the 2026-08-24 phone-collision 500s
        // were retried 4x because nothing appeared). Surface the backend's
        // guiding message; alert() matches the split-payment precedent above.
        const message =
          error?.response?.data?.error ??   // our { status, error } shape
          error?.response?.data?.detail ??  // FastAPI HTTPException shape
          t("appointment.modal.save_failed", "Could not save the appointment. Please try again.");
        alert(message);
      },
    });
  }

  const renderSection = () => {
    switch (activeSection) {
      case "status":
        return <StatusSection />
      case "payment":
        return <PaymentSection />
      case "notifications":
        return <NotificationsSection />
      case "consumables":
        return <ConsumablesSection />
      case "client-data":
        return <ClientDataSection />
      case "client-notes":
        return <ClientNotesSection />
      case "visit-history":
        return <VisitHistorySection />
      case "messages":
        return <MessagesSection />
      case "changes":
        return <ChangesSection />
      case "details":
      default:
        return <DetailsSection />
    }
  }


  // Only render modal content if open, appointmentId, and appointmentData are set
  const shouldShowContent = open && appointmentId && appointmentData && !isDetailsLoading

  return (
    <Dialog open={open} onOpenChange={closeModal}>
      <DialogContent className="max-w-[1200px] p-0 overflow-hidden rounded-lg">
        <DialogTitle>
          <span className="sr-only">{t("appointment.modal.title", "Appointment Details")}</span>
        </DialogTitle>
        <DialogDescription>
          <span className="sr-only">{t("appointment.modal.description", "View and edit appointment details")}</span>
        </DialogDescription>
        <div className="flex h-[96vh]">
          {shouldShowContent ? (
            <>
              <ModalSidebar activeSection={activeSection} onSectionChange={setActiveSection} clientName={appointmentData.client.name}
                            clientId={Number(appointmentData.client?.id) || null} />
              <div className="flex-1 flex flex-col">
                {isEditing && (
                  <div className="w-full bg-amber-100 text-amber-800 text-center py-2 text-xs font-medium z-20">
                    updating
                  </div>
                )}
                {renderSection()}
                <ModalFooter
                  appointmentId={appointment?.id}
                  onDelete={handleDelete}
                  onSave={handleSave}
                  isSaving={isEditing}
                  disableSave={isSplitInvalid}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <span className="text-muted-foreground">
                {isDetailsLoading ? t("appointment.modal.loading", "Loading appointment data...") : t("appointment.modal.no_selection", "No appointment selected.")}
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
